import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import { useIntercom } from "react-use-intercom";

import SkeletonLoader from "../../Components/Skeleton/skeleton-loader.js";
import WidgetFormSkeleton from "../../Components/Skeleton/widget-form-skeleton.js";
import SegmentControl from "../../Components/Buttons/segment-control";
import OutlineButton from "../../Components/Buttons/outline-button.js";
import PrimaryButton from "../../Components/Buttons/primary-button.js";

import renderSetPin from "../../Organisms/Modals/Stake Debit/set-pin.js";
import renderCardStatusReason from "../../Organisms/Modals/Stake Debit/card-status-reason.js";
import renderTerminateCard from "../../Organisms/Modals/Stake Debit/terminate-card.js";

import { useModalContext } from "../../Contexts/ModalContext.js";

import { useStakeDebit, useCardWidgetToken } from "../../CustomHooks/Queries";
import { useRequestStakeDebitCard } from "../../CustomHooks/Mutations";
import {
  SYNCTERA_ENV,
  readSyncteraBrandStyles,
  useSyncteraWidgetScripts,
} from "../../CustomHooks/useSyncteraWidgets";

import { dispatchErrorToast, dispatchSuccessToast, isSVDApproved } from "../../Utils/helpers.js";
import { CARD_WIDGET_TOKEN_REFRESH_MS } from "../../Utils/constants";

import { ArrowLeftCircle, CardFilled, CircleExclamation, Lock, Retry } from "../../Assets/Icons/index.js";

import "./manage-stake-debit.scss";

const cardTypes = [
  { label: "Virtual Card", value: "virtual" },
  { label: "Physical Card", value: "physical" },
];

// How often to re-ask for stake_debit while waiting on CARD.UPDATED to confirm a new
// PIN, and how long to keep asking before giving up on the webhook.
const PIN_CONFIRMATION_POLL_MS = 3 * 1000;
const PIN_CONFIRMATION_WINDOW_MS = 60 * 1000;

function WidgetCardSkeleton({ isRefreshing = false }) {
  return (
    <div className="widget-card-skeleton" data-refreshing={isRefreshing}>
      <div className="widget-card-skeleton__top">
        <SkeletonLoader className="border--rounded-md" width="40px" height="32px" />
        <SkeletonLoader className="border--rounded-full" width="92px" height="24px" />
      </div>
      <div className="widget-card-skeleton__middle">
        <SkeletonLoader className="border--rounded-sm" width="100%" height="14px" />
      </div>
      <div className="widget-card-skeleton__bottom">
        <SkeletonLoader className="border--rounded-sm" width="140px" height="14px" />
        <div className="widget-card-skeleton__network">
          <SkeletonLoader className="border--rounded-full" width="22px" height="22px" />
          <SkeletonLoader className="border--rounded-full" width="22px" height="22px" />
        </div>
      </div>
      {isRefreshing && (
        <div className="widget-card-skeleton__refresh" role="status" aria-live="polite">
          <Retry className="widget-card-skeleton__refresh-icon" />
          <span>Refreshing card…</span>
        </div>
      )}
    </div>
  );
}

function RevealCard({
  token,
  cardId,
  cardForm,
  styles,
  lastFour,
  embossName,
  refetchToken,
  cardStatus,
  isPinSet = true,
  extraActions,
}) {
  const revealCardRef = useRef(null);
  const [hasError, setHasError] = useState(false);

  const isFrozen = cardStatus === "SUSPENDED";
  // A frozen card blocks everything anyway, and Set PIN is hidden while frozen, so
  // the PIN warning would have nothing to act on.
  const showPinWarning = !isPinSet && !isFrozen;

  useEffect(() => {
    setHasError(false);
  }, [token]);

  useEffect(() => {
    const el = revealCardRef.current;
    if (!el || !token) return;

    const handleError = () => {
      setHasError(true);
      refetchToken();
    };
    el.onError = handleError;
    el.onFailure = handleError;

    return () => {
      el.onError = null;
      el.onFailure = null;
    };
  }, [token, cardId, refetchToken]);

  if (!token || hasError) return <WidgetCardSkeleton isRefreshing={hasError} />;

  return (
    <div className="card-container">
      {isFrozen && (
        <div className="card-frozen-badge" role="status" aria-live="polite">
          <Lock className="card-frozen-badge__icon" />
          <span>Card frozen — transactions are blocked</span>
        </div>
      )}
      {showPinWarning && (
        <div className="card-pin-warning" role="status" aria-live="polite">
          <CircleExclamation className="card-pin-warning__icon" />
          <span>No PIN set — purchases at stores and ATMs that ask for one can be declined</span>
        </div>
      )}
      <div className="card-wrapper" data-frozen={isFrozen}>
        <reveal-card
          ref={revealCardRef}
          token={token}
          env={SYNCTERA_ENV}
          styles={styles}
          card-form={cardForm}
          card-type="DEBIT"
          card-network="mastercard"
          last-four={lastFour}
          emboss-name={embossName}
        />
      </div>
      <div className="card-actions">
        <OutlineButton onClick={() => revealCardRef.current?.toggleCardSide()}>Flip Card</OutlineButton>
        {extraActions}
      </div>
    </div>
  );
}

function CardStatusActions({ cardId, cardStatus, onChanged }) {
  const modalContext = useModalContext();

  const isFrozen = cardStatus === "SUSPENDED";

  // Both freeze and unfreeze require a reason (enum), collected in a dedicated modal
  // before the request fires.
  const onToggleFreeze = () =>
    renderCardStatusReason(modalContext, {
      cardId,
      status: isFrozen ? "ACTIVE" : "SUSPENDED",
      onChanged,
    });

  return (
    <>
      <OutlineButton onClick={onToggleFreeze}>{isFrozen ? "Unfreeze Card" : "Freeze Card"}</OutlineButton>
      <OutlineButton
        className="terminate-card-button"
        onClick={() => renderTerminateCard(modalContext, { cardId, onTerminated: onChanged })}
      >
        Terminate Card
      </OutlineButton>
    </>
  );
}

function TerminatedCardPlaceholder() {
  return (
    <div className="terminated-card-placeholder">
      <CircleExclamation className="terminated-card-placeholder__icon" />
      <h4 className="terminated-card-placeholder__title">This card has been terminated</h4>
      <p className="terminated-card-placeholder__subtitle">
        This card is permanently deactivated and can no longer be used.
      </p>
    </div>
  );
}

function ActivateSection({ cardId, styles, onActivated }) {
  const ref = useRef(null);
  const [hasError, setHasError] = useState(false);

  const { data: tokenData, refetch: refetchToken } = useCardWidgetToken(
    { cardId, widgetType: "ACTIVATE" },
    { enabled: !!cardId, staleTime: 0, refetchInterval: CARD_WIDGET_TOKEN_REFRESH_MS },
  );
  const token = tokenData?.widget_token;

  useEffect(() => {
    setHasError(false);
  }, [token]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !token) return;

    el.onError = () => {
      setHasError(true);
      refetchToken && refetchToken();
    };
    el.onSuccess = () => {
      dispatchSuccessToast("Card activated successfully", { key: `activate-${cardId}` });
      onActivated && onActivated();
    };
    // The Synctera <activate-card> widget renders its own failure message inline in
    // the widget, so we deliberately do not dispatch a toast here to avoid showing the
    // same error twice. We still refresh the (now consumed) widget token so the user
    // can retry.
    el.onFailure = () => {
      refetchToken && refetchToken();
    };

    return () => {
      el.onError = null;
      el.onSuccess = null;
      el.onFailure = null;
    };
  }, [token, cardId, onActivated, refetchToken]);

  const isLoading = !token || hasError;

  return (
    <div className="widget-section">
      {isLoading && <WidgetFormSkeleton isRefreshing={hasError} fieldCount={2} />}
      <div className="widget-section-body" data-loading={isLoading}>
        {token && (
          <activate-card
            ref={ref}
            token={token}
            env={SYNCTERA_ENV}
            theme="night-shift"
            styles={styles}
            custom-labels='{
            "widgetTitle": "Activate Your New Card",
            "cardNumberLabel": "Card Number (PAN)",
            "securityCodeLabel": "Security Code",
            "submitButtonText": "Activate Now",
            "submitLoadingText": "Processing..."
          }'
          ></activate-card>
        )}
      </div>
    </div>
  );
}

function RequestPhysicalCardSection({ onRequested }) {
  const { mutate: requestPhysicalCard, isLoading } = useRequestStakeDebitCard({
    onSuccess: () => {
      dispatchSuccessToast("Your physical card is on its way!", { key: "request-physical-card" });
      onRequested && onRequested();
    },
    onError: (e) => {
      dispatchErrorToast(e?.response?.data?.message || "Unable to request a physical card. Please try again.", {
        key: "request-physical-card",
      });
    },
  });

  return (
    <div className="request-card-section">
      <div className="request-card-illustration">
        <CardFilled className="request-card-illustration__icon" />
      </div>
      <div className="request-card-copy">
        <h4 className="request-card-title">Order your physical Stake debit card</h4>
        <p className="request-card-subtitle">
          We&apos;ll ship it to your address on file. Most cards arrive within 7–10 business days.
        </p>
      </div>
      <PrimaryButton className="request-card-cta" onClick={() => requestPhysicalCard("PHYSICAL")} disabled={isLoading}>
        {isLoading ? "Requesting…" : "Request Physical Card"}
      </PrimaryButton>
    </div>
  );
}

function RequestVirtualCardSection({ onRequested }) {
  const { mutate: requestVirtualCard, isLoading } = useRequestStakeDebitCard({
    onSuccess: () => {
      dispatchSuccessToast("Your virtual card is ready!", { key: "request-virtual-card" });
      onRequested && onRequested();
    },
    onError: (e) => {
      dispatchErrorToast(e?.response?.data?.message || "Unable to request a virtual card. Please try again.", {
        key: "request-virtual-card",
      });
    },
  });

  return (
    <div className="request-card-section">
      <div className="request-card-illustration">
        <CardFilled className="request-card-illustration__icon" />
      </div>
      <div className="request-card-copy">
        <h4 className="request-card-title">Request your virtual Stake debit card</h4>
        <p className="request-card-subtitle">
          Use it online and in your mobile wallet right away — no waiting on the mail.
        </p>
      </div>
      <PrimaryButton className="request-card-cta" onClick={() => requestVirtualCard("VIRTUAL")} disabled={isLoading}>
        {isLoading ? "Requesting…" : "Request Virtual Card"}
      </PrimaryButton>
    </div>
  );
}

export default function ManageStakeDebit() {
  useSyncteraWidgetScripts();

  const navigate = useNavigate();
  const { show: showIntercom } = useIntercom();
  const modalContext = useModalContext();

  // Which card form is waiting on Synctera to confirm a PIN we just set. Declared ahead
  // of the query because refetchInterval is read during render.
  const [pinAwaitingForm, setPinAwaitingForm] = useState(null);

  const { isLoading: isLoadingStakeDebit, data: stakeDebitData, refetch: refetchStakeDebit } = useStakeDebit({
    refetchInterval: pinAwaitingForm ? PIN_CONFIRMATION_POLL_MS : false,
  });
  const debit = stakeDebitData?.data?.data ?? {};
  const isApproved = isSVDApproved(stakeDebitData?.data);

  const physicalCardId = debit.card_id;
  const virtualCardId = debit.virtual_card_id;
  const isPhysicalActivated = debit.status === "DebitCardActivated";
  const vritualLastFour = debit.virtual_last_4_digits;
  const physicalLastFour = debit.physical_last_4_digits;
  const virtualEmbossName = debit.virtual_cardholder_name;
  const physicalEmbossName = debit.physical_cardholder_name;
  const virtualCardStatus = debit.virtual_card_status;
  const physicalCardStatus = debit.card_status;
  const isVirtualTerminated = virtualCardStatus === "TERMINATED";
  const isPhysicalTerminated = physicalCardStatus === "TERMINATED";
  // Synctera reports is_pin_set through the CARD.UPDATED webhook, so treat a missing
  // flag as "no PIN" rather than assuming one is set.
  const isVirtualPinSet = debit.is_virtual_card_pin_set ?? false;
  const isPhysicalPinSet = debit.is_physical_card_pin_set ?? false;

  const [cardToRender, setCardToRender] = useState("virtual");

  // is_pin_set only reaches us through the CARD.UPDATED webhook, which lands after the
  // widget reports success, so the first refetch usually still says "no PIN". Poll until
  // Synctera confirms, and give up after the window so this does not run forever if the
  // webhook never arrives.
  useEffect(() => {
    if (!pinAwaitingForm) return;
    const isConfirmed = pinAwaitingForm === "virtual" ? isVirtualPinSet : isPhysicalPinSet;
    if (isConfirmed) {
      setPinAwaitingForm(null);
      return;
    }
    const timer = setTimeout(() => setPinAwaitingForm(null), PIN_CONFIRMATION_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [pinAwaitingForm, isVirtualPinSet, isPhysicalPinSet]);

  // An approved account with no cards yet still belongs here — that is where it
  // requests its first virtual card.
  useEffect(() => {
    if (isLoadingStakeDebit) return;
    if (!physicalCardId && !virtualCardId && !isApproved) navigate(-1);
  }, [isLoadingStakeDebit, physicalCardId, virtualCardId, isApproved, navigate]);

  const revealStyles = useMemo(() => readSyncteraBrandStyles({ variant: "reveal" }), []);
  const formStyles = useMemo(() => readSyncteraBrandStyles({ variant: "form" }), []);

  const needsRevealToken =
    (cardToRender === "virtual" && !!virtualCardId && !isVirtualTerminated) ||
    (cardToRender === "physical" && !!physicalCardId && isPhysicalActivated && !isPhysicalTerminated);

  const { data: widgetTokenData, refetch: refetchToken } = useCardWidgetToken(
    {
      cardId: cardToRender === "virtual" ? virtualCardId : physicalCardId,
      widgetType: "REVEAL",
    },
    {
      enabled: needsRevealToken,
      staleTime: 0,
      refetchInterval: CARD_WIDGET_TOKEN_REFRESH_MS,
    },
  );

  const token = widgetTokenData?.widget_token;

  return (
    <div className="manage-stake-debit">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <div className="section-header">
              <button className="go-back-button" onClick={() => navigate(-1)} aria-label="Go back">
                <ArrowLeftCircle />
              </button>
              <h3 className="section-title">Manage Cards</h3>
              <div className="dummy"></div>
            </div>
            <div className="content">
              <div className="top-container d-flex flex-column">
                <SegmentControl
                  className="mb-5"
                  value={cardToRender}
                  onChange={(_, value) => value && setCardToRender(value)}
                  exclusive
                  segmentOptions={cardTypes}
                />
                <Stack className="cards-container">
                  {cardToRender === "virtual" &&
                    (!virtualCardId ? (
                      <RequestVirtualCardSection onRequested={refetchStakeDebit} />
                    ) : isVirtualTerminated ? (
                      <TerminatedCardPlaceholder />
                    ) : (
                      <RevealCard
                        token={token}
                        cardId={virtualCardId}
                        cardForm="virtual"
                        styles={revealStyles}
                        lastFour={vritualLastFour}
                        embossName={virtualEmbossName}
                        refetchToken={refetchToken}
                        cardStatus={virtualCardStatus}
                        isPinSet={isVirtualPinSet}
                        extraActions={
                          <>
                            {virtualCardStatus !== "SUSPENDED" && (
                              <OutlineButton
                                onClick={() =>
                                  renderSetPin(modalContext, {
                                    title: isVirtualPinSet ? "Change PIN" : "Set PIN",
                                    cardId: virtualCardId,
                                    styles: formStyles,
                                    onSuccess: () => setPinAwaitingForm("virtual"),
                                  })
                                }
                              >
                                {isVirtualPinSet ? "Change PIN" : "Set PIN"}
                              </OutlineButton>
                            )}
                            <CardStatusActions
                              cardId={virtualCardId}
                              cardStatus={virtualCardStatus}
                              onChanged={refetchStakeDebit}
                            />
                          </>
                        }
                      />
                    ))}
                  {cardToRender === "physical" && (
                    <>
                      {!physicalCardId ? (
                        <RequestPhysicalCardSection onRequested={refetchStakeDebit} />
                      ) : isPhysicalTerminated ? (
                        <TerminatedCardPlaceholder />
                      ) : !isPhysicalActivated ? (
                        <ActivateSection cardId={physicalCardId} styles={formStyles} onActivated={refetchStakeDebit} />
                      ) : (
                        <RevealCard
                          token={token}
                          cardId={physicalCardId}
                          cardForm="physical"
                          styles={revealStyles}
                          lastFour={physicalLastFour}
                          embossName={physicalEmbossName}
                          refetchToken={refetchToken}
                          cardStatus={physicalCardStatus}
                          isPinSet={isPhysicalPinSet}
                          extraActions={
                            <>
                              {physicalCardStatus !== "SUSPENDED" && (
                                <OutlineButton
                                  onClick={() =>
                                    renderSetPin(modalContext, {
                                      title: isPhysicalPinSet ? "Change PIN" : "Set PIN",
                                      cardId: physicalCardId,
                                      styles: formStyles,
                                      onSuccess: () => setPinAwaitingForm("physical"),
                                    })
                                  }
                                >
                                  {isPhysicalPinSet ? "Change PIN" : "Set PIN"}
                                </OutlineButton>
                              )}
                              <CardStatusActions
                                cardId={physicalCardId}
                                cardStatus={physicalCardStatus}
                                onChanged={refetchStakeDebit}
                              />
                            </>
                          }
                        />
                      )}
                    </>
                  )}
                </Stack>
              </div>
              <div className="bottom-container">
                <p className="card-support-help">
                  Please call us at{" "}
                  <a href="tel:+19062545135">1-906-254-5135</a> or email us at{" "}
                  <a href="mailto:hello@getyourstake.com">hello@getyourstake.com</a> if you have any questions about
                  your card.
                </p>
                <OutlineButton className="green-theme" onClick={showIntercom}>
                  Need to close Stake Checking?<br></br>Contact Support
                </OutlineButton>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
